import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Bell,
  CheckCheck,
  LoaderCircle,
  LogIn,
  LogOut,
  Menu,
  Moon,
  Shield,
  Sun,
  Trash2,
  X,
} from "lucide-react";
import { logout as apiLogout } from "@/lib/api";
import { applyExplicitLogoutBoundary } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { Logo } from "@/components/brand/Logo";
import { useNotifications } from "@/features/notifications/useNotifications";
import { useAccountPhoto } from "@/features/account/useAccountPhoto";
import {
  groupNotifications,
  type NotificationRow as NotificationRowModel,
} from "@/features/notifications/groupNotifications";
import type { AppNotification, MePayload } from "@/lib/types";
import { getPublicNavCapabilities } from "./navCapabilities";
import {
  getInitialTheme,
  setTheme,
  type ThemePreference,
} from "./themePreference";
import { useBrandSync } from "./useBrandSync";
import { cn } from "@/lib/cn";
import { apexDomain, hostKind } from "@/lib/hostKind";

export function getPublicHeaderLoginNext(pathname: string, fullPath = pathname): string {
  const routePath = pathname || "/";
  if (
    routePath === "/" ||
    /^\/(?:login|reset-password|verify-email)(?:\/|$)/.test(routePath)
  ) {
    return "/hub";
  }
  return fullPath || routePath;
}

export function parseNotificationLink(
  link: string,
): { pathname: string; search: Record<string, string>; hash: string } | null {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(link, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return {
      pathname: url.pathname,
      search: Object.fromEntries(url.searchParams.entries()),
      hash: url.hash,
    };
  } catch {
    return null;
  }
}

/** Shared app header rendered at the root layout so every route gets
 * the same nav + auth boundary + theme toggle.
 *
 * Product branding (mark + wordmark) is bundled with the app — see
 * `web/public/brand/`. The product name `app_name` ("tktic") is
 * fixed and is NOT operator-configurable — the inline wordmark
 * always says "tktic", and the backend rejects PUTs against the
 * `app_name` settings key (see `tests/test_settings.py::
 * test_app_name_is_not_writable`).
 *
 * Operator branding is a separate concern: `club_title` and the
 * uploaded `favicon_url` flow into the browser
 * tab (title + favicon) via `useBrandSync` below. That's about the
 * operator's club identity on the tab strip, not the product name.
 *
 * Public pages stay anonymous-readable; signed-in users see the
 * role-gated Coach/My Feedback/Admin links.
 */
export function PublicAppHeader() {
  useMe();
  useBrandSync();
  const authMe = useAuthStore((s) => s.me);
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, setThemeState] = useState<ThemePreference>(() => getInitialTheme());
  const [signOutPending, setSignOutPending] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const notifications = useNotifications();

  // Phase C: open a notification (single or grouped) — mark its
  // key(s) read, close the panel, then route. SPA-navigate in-app
  // links, splitting query strings into TanStack's `search` object;
  // keep a full load for `#hash` anchors so the browser scrolls to
  // the admin People deep-link target.
  const openNotificationTarget = (keys: string[], link: string) => {
    void notifications.markRead(keys).finally(() => {
      setNotificationsOpen(false);
      const target = parseNotificationLink(link);
      if (!target || target.hash) {
        window.location.assign(link);
      } else {
        void navigate({ to: target.pathname, search: target.search });
      }
    });
  };

  const caps = getPublicNavCapabilities(authMe);
  const identityLabel = getHeaderIdentityLabel(authMe, caps.displayName);
  const accountPhotoUrl = useAccountPhoto(authMe?.profile?.photo_filename);
  const currentPath = location.href || `${location.pathname}${location.searchStr}` || "/";
  const currentPathname = location.pathname || "/";
  const loginNext = getPublicHeaderLoginNext(currentPathname, currentPath);
  // #215 — primary nav explicitly sized for WCAG 2.5.5 / iOS HIG
  // touch targets on mobile (`min-h-11` = 44px) and back to the dense
  // chrome on ≥sm (640px). `inline-flex items-center` so the
  // explicit min-h vertically centers the link text rather than
  // leaving it top-aligned.
  //
  // Computed: with `text-xs` (12px) × `leading-base` (1.55) the line
  // height is ~18.6px, so `py-2` (16px total) by itself only renders
  // ~35px — the prior commit's "browser adds tap-target padding"
  // claim was wrong (UAs don't reliably add it around inline links).
  // `min-h-11` forces the 44px floor independent of font metrics.
  // Same pattern below for `iconControlClass`. The dense admin
  // console can keep tighter controls internally — only this
  // shared chrome is bumped.
  const navLinkClass = (active: boolean) =>
    cn(
      "inline-flex min-h-11 items-center justify-center rounded border px-2.5 py-2 transition-colors sm:min-h-0 sm:py-1",
      active
        ? "border-accent bg-accent text-accent-ink"
        : "border-rule text-ink-muted hover:border-accent hover:bg-field hover:text-ink",
    );
  const iconControlClass = (tone: "neutral" | "destructive" | "accent" = "neutral") =>
    cn(
      "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors sm:h-8 sm:w-8",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50",
      tone === "accent"
        ? "bg-accent text-accent-ink hover:bg-accent-hover"
        : tone === "destructive"
          ? "text-destructive hover:bg-destructive/10"
          : "text-ink-muted hover:bg-field hover:text-ink",
    );
  const isTeamsActive =
    currentPathname.startsWith("/teams") || currentPathname.startsWith("/match/");
  const isCoachActive = currentPathname.startsWith("/coach");
  const isFeedbackActive = currentPathname.startsWith("/feedback");
  const isAdminActive = currentPathname.startsWith("/admin");
  const isPlatformActive = currentPathname.startsWith("/super-admin");
  const isDocsActive = currentPathname.startsWith("/docs");

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPath]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (notificationsRef.current?.contains(event.target as Node)) return;
      setNotificationsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNotificationsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [notificationsOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (headerRef.current?.contains(event.target as Node)) return;
      setMobileMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileMenuOpen]);

  const toggleTheme = () => {
    setThemeState((prev) => setTheme(prev === "dark" ? "light" : "dark"));
  };

  const themeToggle = (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="public-header-theme-toggle"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      className={iconControlClass()}
    >
      {theme === "dark" ? (
        <Sun size={16} strokeWidth={2} aria-hidden="true" />
      ) : (
        <Moon size={16} strokeWidth={2} aria-hidden="true" />
      )}
    </button>
  );

  const signOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      try {
        // /api/logout revokes the app session AND the Kratos session
        // (cookie-backed), so one call covers every provider.
        await apiLogout();
      } catch {
        // Server-side session may already be gone; local cleanup is authoritative.
      }
      applyExplicitLogoutBoundary();
      void navigate({ to: "/", replace: true });
    } finally {
      setSignOutPending(false);
    }
  };

  // Signed out, the login control is suppressed on the APEX only: a login
  // implies a session for *some* club and the apex doesn't know which, so
  // apex `/login` is a dead end (HostGuard bounces it back to the landing).
  // The ADMIN host KEEPS it — that host is operator-login-only, and this
  // header renders on its `/login` page. Signed-in actions stay host-agnostic:
  // an operator with a live session still needs sign-out wherever they are.
  const authActions = caps.signedIn ? (
    <>
      <NotificationBell
        open={notificationsOpen}
        onOpenChange={(open) => {
          setNotificationsOpen(open);
          if (open) setMobileMenuOpen(false);
        }}
        controlClass={iconControlClass()}
        notifications={notifications.data?.notifications ?? []}
        unreadCount={notifications.data?.unread_count ?? 0}
        loading={notifications.isLoading}
        containerRef={notificationsRef}
        onMarkAllRead={() => void notifications.markAllRead()}
        onClearAll={() => void notifications.clearAll()}
        onDismiss={(key) => void notifications.dismiss(key)}
        onOpenNotification={(item) =>
          openNotificationTarget([item.key], item.link)
        }
        onOpenGroup={(group) => openNotificationTarget(group.keys, group.link)}
      />
      <Link
        to="/me"
        data-testid="public-header-account-link"
        aria-label={`Account: ${identityLabel || caps.displayName || "signed in user"}`}
        title={identityLabel ? `Account: ${identityLabel}` : caps.displayName || "Account"}
        className="group inline-flex h-11 max-w-[12rem] shrink-0 items-center gap-2 rounded bg-field px-2 text-ink transition-colors hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:h-8 sm:px-1.5 sm:pr-2.5"
      >
        {accountPhotoUrl ? (
          <img
            src={accountPhotoUrl}
            alt=""
            className="h-7 w-7 shrink-0 rounded-full border border-rule object-cover sm:h-6 sm:w-6"
          />
        ) : (
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink text-[0.64rem] font-display font-semibold leading-none text-ink-inverse transition-colors group-hover:bg-accent group-hover:text-accent-ink sm:h-6 sm:w-6">
            {getIdentityInitials(identityLabel || caps.displayName)}
          </span>
        )}
        <span
          className="hidden min-w-0 max-w-[8.5rem] truncate text-xs font-display uppercase tracking-wider text-ink sm:inline"
          data-testid="public-header-identity"
        >
          {getShortIdentityLabel(identityLabel || caps.displayName)}
        </span>
      </Link>
      <button
        type="button"
        onClick={() => void signOut()}
        disabled={signOutPending}
        data-testid="public-header-logout"
        aria-label={signOutPending ? "Signing out" : "Log out"}
        title={signOutPending ? "Signing out" : "Log out"}
        className={cn(iconControlClass("destructive"), "hidden sm:inline-flex")}
      >
        {signOutPending ? (
          <LoaderCircle size={16} strokeWidth={2} aria-hidden="true" className="animate-spin" />
        ) : (
          <LogOut size={16} strokeWidth={2} aria-hidden="true" />
        )}
      </button>
    </>
  ) : hostKind() !== "apex" ? (
    <Link
      to="/login"
      search={{ next: loginNext }}
      data-testid="public-header-login-link"
      aria-label="Log in"
      title="Log in"
      className={iconControlClass("accent")}
    >
      <LogIn size={16} strokeWidth={2} aria-hidden="true" />
    </Link>
  ) : null;

  const renderAdminLink = (extraClass?: string) =>
    caps.canOpenAdmin ? (
      <Link
        to="/admin"
        data-testid="public-header-admin-link"
        className={cn(navLinkClass(isAdminActive), extraClass)}
        onClick={() => setMobileMenuOpen(false)}
      >
        Admin
      </Link>
    ) : null;

  const renderTeamsLink = () => (
    <Link
      to="/teams"
      data-testid="public-header-teams-link"
      className={navLinkClass(isTeamsActive)}
      onClick={() => setMobileMenuOpen(false)}
    >
      Teams
    </Link>
  );

  // Platform super-admin console. Distinct from the per-club Admin link: a
  // super-admin manages every club here (`/super-admin`), and on the apex /
  // super-admin host this is the ONLY operator surface (the club-scoped
  // Coach / My Feedback / Admin links collapse — there is no host club).
  //
  // On a CLUB host the console is unreachable: `/super-admin` renders through
  // the root route, but a club host resolves `request.state.club` and the
  // console is not a club-scoped surface — a relative SPA <Link> kept the
  // browser on `<club>.<apex>/super-admin`, which 404s. Cross to the operator
  // host with a plain <a> and an absolute URL (a genuine href keeps
  // middle-click / copy-link / the status-bar preview working, which a router
  // <Link> + window.location handler would not). The host comes from the
  // trusted literal `admin` + the build's apex — no user input — mirroring
  // `goToAdminHost`.
  //
  // The APEX crosses hosts too: `SuperAdminShell` is admin-host-only,
  // so the console 404s there like on any club host. Only the operator host
  // itself keeps the relative SPA route.
  const renderPlatformLink = (extraClass?: string) => {
    if (!caps.canOpenPlatform) return null;
    const platformClass = cn(navLinkClass(isPlatformActive), extraClass);
    const label = (
      <>
        <Shield size={14} strokeWidth={2} aria-hidden="true" className="mr-1" />
        Platform
      </>
    );
    return hostKind() === "admin" ? (
      <Link
        to="/super-admin"
        data-testid="public-header-platform-link"
        className={platformClass}
        onClick={() => setMobileMenuOpen(false)}
      >
        {label}
      </Link>
    ) : (
      <a
        href={`https://admin.${apexDomain()}/super-admin`}
        data-testid="public-header-platform-link"
        className={platformClass}
        onClick={() => setMobileMenuOpen(false)}
      >
        {label}
      </a>
    );
  };

  // Any signed-in user can reach the help center, regardless of role.
  const renderDocsLink = (extraClass?: string) =>
    caps.signedIn ? (
      <Link
        to="/docs"
        data-testid="public-header-docs-link"
        className={cn(navLinkClass(isDocsActive), extraClass)}
        onClick={() => setMobileMenuOpen(false)}
      >
        Help
      </Link>
    ) : null;

  // Teams is a CLUB host's public team index — there's no club to resolve
  // teams from on the apex / admin host, so the link is hidden there.
  const showTeams = hostKind() === "club";

  const primaryNav = (
    <>
      {showTeams ? renderTeamsLink() : null}
      {caps.canOpenCoach ? (
        <Link
          to="/coach"
          data-testid="public-header-coach-link"
          className={navLinkClass(isCoachActive)}
          onClick={() => setMobileMenuOpen(false)}
        >
          Coach
        </Link>
      ) : null}
      {caps.canOpenFeedback ? (
        <Link
          to="/feedback"
          data-testid="public-header-feedback-link"
          className={navLinkClass(isFeedbackActive)}
          onClick={() => setMobileMenuOpen(false)}
        >
          My Feedback
        </Link>
      ) : null}
      {renderAdminLink()}
      {renderPlatformLink()}
      {renderDocsLink()}
    </>
  );

  const renderMobileMenuPanel = (navContent: ReactNode) =>
    caps.signedIn && mobileMenuOpen ? (
      <div
        id="public-header-mobile-menu-panel"
        data-testid="public-header-mobile-menu-panel"
        className="border-t border-rule bg-surface px-4 pb-3 pt-2 sm:px-6 md:hidden"
      >
        <nav
          aria-label="Mobile navigation"
          className="grid grid-cols-2 gap-2 text-xs font-display uppercase tracking-wider"
        >
          {navContent}
          <button
            type="button"
            onClick={() => void signOut()}
            disabled={signOutPending}
            data-testid="public-header-mobile-logout"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded border border-rule px-2.5 py-2 text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50 sm:hidden"
          >
            {signOutPending ? (
              <LoaderCircle size={16} strokeWidth={2} aria-hidden="true" className="animate-spin" />
            ) : (
              <LogOut size={16} strokeWidth={2} aria-hidden="true" />
            )}
            Log out
          </button>
        </nav>
      </div>
    ) : null;

  const mobileMenuButton = caps.signedIn ? (
    <button
      type="button"
      onClick={() => {
        setNotificationsOpen(false);
        setMobileMenuOpen((open) => !open);
      }}
      data-testid="public-header-mobile-menu"
      aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
      aria-expanded={mobileMenuOpen}
      aria-controls="public-header-mobile-menu-panel"
      title={mobileMenuOpen ? "Close menu" : "Menu"}
      className={cn(iconControlClass(), "md:hidden")}
    >
      {mobileMenuOpen ? (
        <X size={18} strokeWidth={2} aria-hidden="true" />
      ) : (
        <Menu size={18} strokeWidth={2} aria-hidden="true" />
      )}
    </button>
  ) : null;

  const mobileMenuPanel = renderMobileMenuPanel(primaryNav);

  return (
    <header
      ref={headerRef}
      data-testid="public-app-header"
      className="sticky top-0 z-50 border-b border-rule bg-surface/95 text-ink backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6 md:py-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/"
            data-testid="public-header-brand"
            aria-label="tktic — home"
            className="flex shrink-0 items-center text-ink transition-colors hover:text-accent"
          >
            <Logo
              size={28}
              data-testid="public-header-app-logo"
            />
          </Link>
          <nav
            aria-label="Public navigation"
            className={cn(
              "min-w-0 items-center gap-2 text-xs font-display uppercase tracking-wider",
              caps.signedIn
                ? "hidden md:flex"
                : "flex overflow-x-auto px-1 py-1",
            )}
          >
            {primaryNav}
          </nav>
        </div>

        <div className="ml-auto flex min-w-0 items-center gap-1.5 text-xs font-display uppercase tracking-wider sm:gap-2">
          {themeToggle}
          {authActions}
          {mobileMenuButton}
        </div>
      </div>
      {mobileMenuPanel}
    </header>
  );
}

function getHeaderIdentityLabel(
  me: MePayload | null | undefined,
  fallback: string | null,
): string | null {
  const firstLast = [me?.profile?.first_name, me?.profile?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  const display = firstLast || me?.user?.display_name?.trim() || fallback?.trim() || "";
  const username = me?.user?.username?.trim();
  if (display && username && display !== username) return `${display} (@${username})`;
  if (username) return `@${username}`;
  return display || null;
}

function getShortIdentityLabel(label: string | null | undefined): string {
  const cleaned = label?.trim();
  if (!cleaned) return "Account";
  const username = cleaned.match(/\(@([^)]+)\)$/)?.[1];
  return username ? `@${username}` : cleaned;
}

function getIdentityInitials(label: string | null | undefined): string {
  const cleaned = label?.replace(/\(@[^)]+\)$/, "").trim();
  if (!cleaned) return "U";
  const username = cleaned.startsWith("@") ? cleaned.slice(1) : cleaned;
  const parts = username.split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return initials || "U";
}

function NotificationBell({
  open,
  onOpenChange,
  controlClass,
  notifications,
  unreadCount,
  loading,
  containerRef,
  onMarkAllRead,
  onClearAll,
  onDismiss,
  onOpenNotification,
  onOpenGroup,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controlClass: string;
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  containerRef: RefObject<HTMLDivElement>;
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onDismiss: (key: string | string[]) => void;
  onOpenNotification: (item: AppNotification) => void;
  onOpenGroup: (group: { link: string; keys: string[] }) => void;
}) {
  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="public-header-notifications"
        aria-label={
          unreadCount > 0
            ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
            : "Notifications"
        }
        title="Notifications"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className={controlClass}
      >
        <Bell size={16} strokeWidth={2} aria-hidden="true" />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-flag px-1 text-[0.6rem] font-display font-semibold leading-none text-navy"
            data-testid="public-header-notifications-count"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          data-testid="notifications-panel"
          className="fixed left-3 right-3 top-16 z-[80] flex max-h-[calc(100svh-5rem)] flex-col rounded border border-rule bg-surface p-2 text-left shadow-pop sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[22rem] sm:max-w-[calc(100vw-2rem)] sm:shadow-sm"
        >
          <div className="flex items-center justify-between gap-2 border-b border-rule pb-2">
            <div>
              <h2 className="font-display text-sm uppercase tracking-wider text-ink">
                Notifications
              </h2>
              <p className="text-xs text-ink-muted">
                {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onMarkAllRead}
                disabled={unreadCount === 0}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-ink disabled:opacity-40"
                aria-label="Mark all notifications read"
                title="Mark all read"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck size={16} strokeWidth={2} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onClearAll}
                disabled={notifications.length === 0}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-destructive disabled:opacity-40"
                aria-label="Clear current notifications"
                title="Clear notifications"
                data-testid="notifications-clear-all"
              >
                <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-2 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto sm:max-h-[28rem]">
            {loading ? (
              <p className="px-2 py-4 text-sm text-ink-muted">Loading…</p>
            ) : notifications.length === 0 ? (
              <p
                className="px-2 py-4 text-sm text-ink-muted"
                data-testid="notifications-empty"
              >
                Nothing needs attention.
              </p>
            ) : (
              groupNotifications(notifications).map((row) =>
                row.kind === "single" ? (
                  <NotificationRow
                    key={row.notification.key}
                    item={row.notification}
                    onOpen={() => onOpenNotification(row.notification)}
                    onDismiss={() => onDismiss(row.notification.key)}
                  />
                ) : (
                  <NotificationGroupRow
                    key={row.id}
                    row={row}
                    onOpen={() =>
                      onOpenGroup({ link: row.link, keys: row.keys })
                    }
                    onDismiss={() => onDismiss(row.keys)}
                  />
                ),
              )
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NotificationRow({
  item,
  onOpen,
  onDismiss,
}: {
  item: AppNotification;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className={[
        "group grid grid-cols-[0.45rem_minmax(0,1fr)_auto] items-start gap-2 rounded px-2 py-2 transition-colors",
        item.is_read ? "hover:bg-field" : "bg-accent/10 hover:bg-accent/15",
      ].join(" ")}
      data-testid="notification-row"
      data-notification-kind={item.kind}
      data-notification-severity={item.severity}
    >
      <span
        className={[
          "mt-1.5 h-2 w-2 rounded-full",
          item.severity === "destructive"
            ? "bg-destructive"
            : item.severity === "flag"
              ? "bg-flag"
              : item.severity === "good"
                ? "bg-good"
                : "bg-accent",
        ].join(" ")}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 text-left"
        data-testid="notification-open"
      >
        <span className="block truncate font-display text-sm text-ink">
          {item.title}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-xs leading-5 text-ink-muted">
          {item.body}
        </span>
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-muted opacity-80 transition-colors hover:bg-field hover:text-ink"
        aria-label="Dismiss notification"
        title="Dismiss"
        data-testid="notification-dismiss"
      >
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}

/** Phase C: a collapsed group of same-kind notifications — one row
 * that opens the shared destination and dismisses every member. */
function NotificationGroupRow({
  row,
  onOpen,
  onDismiss,
}: {
  row: Extract<NotificationRowModel, { kind: "group" }>;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className={[
        "group grid grid-cols-[0.45rem_minmax(0,1fr)_auto] items-start gap-2 rounded px-2 py-2 transition-colors",
        row.isRead ? "hover:bg-field" : "bg-accent/10 hover:bg-accent/15",
      ].join(" ")}
      data-testid="notification-group-row"
      data-notification-count={row.count}
    >
      <span
        className="mt-1.5 h-2 w-2 rounded-full bg-accent"
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 text-left"
        data-testid="notification-group-open"
      >
        <span className="block truncate font-display text-sm text-ink">
          {row.title}
        </span>
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-muted opacity-80 transition-colors hover:bg-field hover:text-ink"
        aria-label="Dismiss notification group"
        title="Dismiss all"
        data-testid="notification-group-dismiss"
      >
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}
